/**
 * SellableItemsMultiSelect
 *
 * A reusable multi-select component for sellable items (plans, groups, products, packages).
 * Used in automation triggers for selecting specific items to match.
 *
 * Pattern mirrors WorkoutPlanSelect but supports multiple selection via Autocomplete.
 */

import { useQuery } from '@tanstack/react-query';
import { useField } from 'formik';
import { FC } from 'react';
import { Autocomplete } from 'src/components/Autocomplete';
import { Chip } from 'src/components/Chip';
import { TextField } from 'src/components/TextField';
import { fetchAllGroups } from 'src/models/group/group';
import { fetchPackageOptions } from 'src/models/package/package';
import { fetchProductOptions } from 'src/models/product/product';
import { fetchWorkoutPlanOptions } from 'src/models/workout-plan/workout-plan';

// =============================================================================
// Types
// =============================================================================

export type SellableType =
  | 'workout_plan'
  | 'group'
  | 'product'
  | 'package'
  | 'client';

export interface SellableItem {
  value: string;
  label: string;
}

// =============================================================================
// Data Fetching
// =============================================================================

/**
 * Maps sellable type to the API fetch function
 */
const SELLABLE_TYPE_FETCHERS: Record<
  SellableType,
  () => Promise<Array<{ id: number | string; name: string }>>
> = {
  workout_plan: fetchWorkoutPlanOptions,
  group: async () => {
    const groups = await fetchAllGroups();
    return groups.map((g) => ({ id: g.id, name: g.name }));
  },
  product: async () => {
    const products = await fetchProductOptions();
    return products.map((p) => ({ id: p.id, name: p.name }));
  },
  package: async () => {
    const packages = await fetchPackageOptions();
    return packages.map((p) => ({ id: p.id, name: p.name }));
  },
  client: async () => [],
};

/**
 * Get the label for a sellable type
 */
export const getSellableTypeLabel = (type: SellableType): string => {
  const labels: Record<SellableType, string> = {
    workout_plan: 'plan(s)',
    group: 'group(s)',
    product: 'product(s)',
    package: 'package(s)',
    client: 'client training',
  };
  return labels[type] || type;
};

/**
 * Hook to fetch sellable items for a given type.
 * Returns empty array when sellableType is null.
 */
export const useSellableItems = (sellableType: SellableType | null) => {
  return useQuery({
    queryKey: ['sellable-items', sellableType],
    queryFn: async (): Promise<SellableItem[]> => {
      if (!sellableType) return [];
      const fetcher = SELLABLE_TYPE_FETCHERS[sellableType];
      const items = await fetcher();
      return items.map((item) => ({
        value: String(item.id),
        label: item.name,
      }));
    },
    enabled: !!sellableType,
    staleTime: 60000,
  });
};

// =============================================================================
// Non-Formik Component
// =============================================================================

interface SellableItemsMultiSelectProps {
  /** The type of sellable item to display */
  sellableType: SellableType;
  /** Currently selected item IDs */
  value: number[];
  /** Callback when selection changes */
  onChange: (ids: number[]) => void;
  /** Optional label */
  label?: string;
  /** Optional placeholder */
  placeholder?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Helper text */
  helperText?: string;
}

export const SellableItemsMultiSelect: FC<SellableItemsMultiSelectProps> = ({
  sellableType,
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  error,
  helperText,
}) => {
  const { data: items = [], isLoading } = useSellableItems(sellableType);

  // Convert IDs to SellableItem objects for the Autocomplete value.
  // Use string comparison since API may return IDs as strings or numbers.
  const selectedItems = items.filter((item) =>
    value.some((v) => String(v) === item.value)
  );

  // Default placeholder based on type
  const defaultPlaceholder = `Select ${getSellableTypeLabel(sellableType)}...`;

  return (
    <Autocomplete<SellableItem, true>
      multiple
      options={items}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      value={selectedItems}
      onChange={(_event, newValue) => {
        const ids = (newValue || []).map((item) => Number(item.value));
        onChange(ids);
      }}
      disabled={isLoading || disabled}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, idx) => (
          <Chip
            size="small"
            label={option.label}
            {...getTagProps({ index: idx })}
            key={option.value}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={
            selectedItems.length === 0 ? placeholder || defaultPlaceholder : ''
          }
          error={error}
          helperText={helperText}
          size="small"
        />
      )}
    />
  );
};

// =============================================================================
// Formik-Connected Component
// =============================================================================

interface FormSellableItemsMultiSelectProps {
  /** Formik field name */
  name: string;
  /** The type of sellable item to display */
  sellableType: SellableType;
  /** Optional label */
  label?: string;
  /** Optional placeholder */
  placeholder?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Optional change handler (called after Formik update) */
  onChange?: (ids: number[]) => void;
}

export const FormSellableItemsMultiSelect: FC<
  FormSellableItemsMultiSelectProps
> = ({ name, sellableType, label, placeholder, disabled, onChange }) => {
  const [field, meta, helpers] = useField<number[]>({ name });

  const handleChange = (ids: number[]) => {
    helpers.setValue(ids);
    onChange?.(ids);
  };

  // Ensure value is always an array
  const value = Array.isArray(field.value) ? field.value : [];

  return (
    <SellableItemsMultiSelect
      sellableType={sellableType}
      value={value}
      onChange={handleChange}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      error={!!(meta.touched && meta.error)}
      helperText={meta.touched ? meta.error : undefined}
    />
  );
};
